// 外部データ(gBiz/有報)の型ゆらぎを吸収する表示用ヘルパー。

interface PeriodLike {
  // 期ラベル（YYYY-MM / 年 / 当期・N期前 など）
  period: string | null;
  // gBiz の「何期前」(0=当期)。BE が付与。小さいほど新しい。年ラベルの行は null。
  periodOffset?: number | null;
}

const offsetOf = (p: PeriodLike): number | null =>
  typeof p.periodOffset === "number" && Number.isFinite(p.periodOffset)
    ? p.periodOffset
    : null;

/**
 * 古い→新しい順の比較関数（全順序）。
 * - periodOffset を持つ行どうしは offset の大きい方（古い）を先にする
 * - periodOffset が無い行（年ラベル）どうしは期ラベルの文字列順
 * - 混在時は periodOffset が無い行を古い側（先）に置く
 * - 同順位は期ラベルで決め、入力順に依存しない
 * @param a 比較対象
 * @param b 比較対象
 * @returns 比較結果
 */
export const byPeriodAsc = (a: PeriodLike, b: PeriodLike): number => {
  const ao = offsetOf(a);
  const bo = offsetOf(b);
  if (ao !== null && bo !== null && ao !== bo) return bo - ao;
  if ((ao === null) !== (bo === null)) return ao === null ? -1 : 1;
  return (a.period ?? "").localeCompare(b.period ?? "");
};

/**
 * 業種などを重複なしの文字列配列にする（単体文字列・オブジェクト要素・null 混在を吸収）
 * @param value 入力値
 * @returns 文字列配列（出現順・重複なし）
 */
export const toStrList = (value: unknown): string[] => {
  if (value === null || value === undefined || value === "") return [];
  const items: unknown[] = Array.isArray(value) ? value : [value];
  const texts = items
    .map((v) => {
      if (v === null || v === undefined) return "";
      if (typeof v === "string") return v;
      if (typeof v === "number" || typeof v === "boolean") return String(v);
      if (typeof v === "object") {
        return Object.values(v)
          .filter(
            (x): x is string | number =>
              typeof x === "string" || typeof x === "number"
          )
          .join(" ");
      }
      return "";
    })
    .map((s) => s.trim())
    .filter((s) => s !== "");
  return [...new Set(texts)];
};

/**
 * グラフ入力用: 有限の number だけ通し、それ以外(NaN/Infinity/文字列/オブジェクト)は null。
 * 数値文字列("123")は number に変換する
 * @param value 入力値
 * @returns 有限の数値または null
 */
export const toFiniteOrNull = (value: unknown): number | null => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};
